import { useState } from 'react';
import { useSession } from '../../auth/session.tsx';
import {
  downloadAnnualReportDraft, downloadFinalAccountDraft, downloadInitialReportDraft, useCourtReportDrafts, useCourtReportOptions,
} from '../../api/courtReports.ts';
import { ApiError } from '../../api/client.ts';

/**
 * Court report drafts: a draft Annual Report, Initial Report or (for a deceased client) Final Account as a Word document, for
 * the System Administrator, where the API has drafts switched on (dev only).
 * Drawn only when the API says so; it refuses anyone else regardless.
 */
export function CourtReportDraft({ clientId }: { clientId: string }) {
  const q = useCourtReportDrafts();
  const { getToken } = useSession();
  // The report filed each May covers the year before.
  const [year, setYear] = useState(() => new Date().getFullYear() - 1);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  // Empty means the default: the CEO role's holder, and the configured county. A choice is sent only when made.
  const [signer, setSigner] = useState('');
  const [county, setCounty] = useState('');
  const options = useCourtReportOptions(q.data?.available === true);
  if (!q.data?.available) return null;
  const choice = { signer: signer || undefined, county: county || undefined };
  const defaultSigner = options.data?.staff.find((s) => s.id === options.data?.signer)?.full_name;
  const defaultCounty = options.data?.counties.find((c) => c.code === options.data?.county)?.label;

  const years = Array.from({ length: 6 }, (_, n) => new Date().getFullYear() - n);
  const generate = async (which: 'annual' | 'initial' | 'final') => {
    setBusy(true);
    setError(null);
    try {
      if (which === 'annual') await downloadAnnualReportDraft(getToken, clientId, year, choice);
      else if (which === 'initial') await downloadInitialReportDraft(getToken, clientId, choice);
      else await downloadFinalAccountDraft(getToken, clientId, choice);
    } catch (e) {
      setError(e instanceof ApiError ? e.message : 'The draft could not be generated.');
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="mt-3 rounded-lg border border-line bg-tint px-3 py-2">
      <h3 className="text-sm font-semibold text-neutral-800">Draft court reports (dev only)</h3>
      <p className="text-sm text-neutral-700">
        Word drafts in Allied’s formats. Everything procura cannot fill is highlighted for the reviewer; the annual
        report’s schedules wait for the finance ledger.
        {q.data.audited === false && ' Generation is not yet written to the audit log (migration 140 is not applied).'}
      </p>
      <div className="mt-2 flex flex-wrap items-center gap-2">
        <label className="flex items-center gap-2 text-sm text-neutral-800">
          Signed by
          <select value={signer} onChange={(e) => setSigner(e.target.value)}
                  className="min-h-[44px] rounded-lg border border-line bg-surface px-2">
            <option value="">{defaultSigner ? `${defaultSigner} (default)` : 'Default (Chief Executive Officer)'}</option>
            {options.data?.staff.filter((s) => s.id !== options.data?.signer).map((s) => <option key={s.id} value={s.id}>{s.full_name}</option>)}
          </select>
        </label>
        <label className="flex items-center gap-2 text-sm text-neutral-800">
          Sworn in
          <select value={county} onChange={(e) => setCounty(e.target.value)}
                  className="min-h-[44px] rounded-lg border border-line bg-surface px-2">
            <option value="">{defaultCounty ? `${defaultCounty} County (default)` : 'Default county'}</option>
            {options.data?.counties.filter((c) => c.code !== options.data?.county).map((c) => <option key={c.code} value={c.code}>{c.label}</option>)}
          </select>
        </label>
      </div>
      <div className="mt-2 flex flex-wrap items-center gap-2">
        <label className="flex items-center gap-2 text-sm text-neutral-800">
          Year covered
          <select value={year} onChange={(e) => setYear(Number(e.target.value))}
                  className="min-h-[44px] rounded-lg border border-line bg-surface px-2">
            {years.map((y) => <option key={y} value={y}>{y}</option>)}
          </select>
        </label>
        <button type="button" onClick={() => void generate('annual')} disabled={busy}
                className="min-h-[44px] rounded-lg bg-brand px-4 font-semibold text-white disabled:bg-neutral-400">
          {busy ? 'Generating…' : 'Annual Report draft'}
        </button>
        <button type="button" onClick={() => void generate('initial')} disabled={busy}
                className="min-h-[44px] rounded-lg border border-line bg-surface px-4 font-semibold text-neutral-900 disabled:text-neutral-500">
          Initial Report draft
        </button>
        <button type="button" onClick={() => void generate('final')} disabled={busy}
                className="min-h-[44px] rounded-lg border border-line bg-surface px-4 font-semibold text-neutral-900 disabled:text-neutral-500">
          Final Account draft
        </button>
      </div>
      {error && <p role="alert" className="mt-2 text-sm font-semibold text-critical-edge">{error}</p>}
    </div>
  );
}
